/**
 * The service page's body: the breadcrumb, the service's name, short line and
 * facts, its description, the studio's preparation note, where it happens and
 * the cancellation line, laid out beside the booking column (the prototype's
 * Option 1). Each line is drawn only when the studio set it.
 */
import type { SiteServiceDto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'service-page',
  description:
    "A service's page: breadcrumb, name, short line, length, format and price, description, preparation note, location and cancellation, beside its photos and booking column.",
  options: {
    aboutHeading: {
      type: 'string',
      default: 'About this service',
      maxLength: 80,
      description: 'The heading over the description; null for none.',
    },
  },
  parts: {
    page: { description: 'The whole page body.', required: false },
    crumbs: { description: 'The breadcrumb: the studio, then the service.', required: false },
    'crumb-home': { description: "The studio's name in the breadcrumb; opens the home page.", required: false },
    'crumb-book': { description: "On a custom site, the site's booking page in the breadcrumb, by its own name; opens it.", required: false },
    'crumb-here': { description: "The service's name in the breadcrumb.", required: false },
    photos: { description: 'Where the photo gallery sits.', required: false },
    name: { description: "The service's name.", required: true, role: 'title' },
    summary: { description: "The service's short line.", required: false },
    facts: { description: 'The row of facts: length, format, price.', required: false },
    duration: { description: 'How long a session lasts.', required: false },
    format: { description: 'In person, online or by phone.', required: false },
    price: { description: 'The price, Free, or from the lowest price.', required: true },
    'about-heading': { description: 'The heading over the description.', required: false },
    description: { description: "The service's full description.", required: false },
    preparation: { description: "The studio's preparation note, under its own heading.", required: false },
    'preparation-heading': { description: "The preparation note's heading, as the studio wrote it.", required: false },
    'preparation-text': { description: 'The preparation note.', required: false },
    details: { description: 'The location and cancellation lines.', required: false },
    location: { description: 'Where the session happens.', required: false },
    cancellation: { description: "The studio's cancellation line.", required: false },
    booking: { description: 'Where the booking column sits.', required: false },
    error: { description: 'The line shown when the page could not load.', required: false },
  },
  actions: {},
  events: {},
});

/** What the service page knows. */
export interface ServicePageState {
  /** loading until the service's read lands; not-found when the studio does not list it (the read answers 4xx); error when the read failed otherwise. */
  readonly status: 'loading' | 'ready' | 'not-found' | 'error';
  /** The service, or null until it lands. */
  readonly service: SiteServiceDto | null;
  /** The studio's name for the breadcrumb, or null until it is known. */
  readonly studioName: string | null;
  /**
   * On a studio's custom site, its own home and booking page, for the breadcrumb (web-studio-sites D13): their
   * labels and addresses as the site names them. Null on the standard website.
   */
  readonly siteLinks: {
    readonly home: { readonly path: string; readonly label: string };
    readonly book: { readonly path: string; readonly label: string };
  } | null;
}

/** The service page takes no action of its own; its photos and booking column are their own pieces. */
export type ServicePageActions = Record<string, never>;

/** The service page reports no event of its own. */
export type ServicePageEvents = Record<string, never>;
